import { h, render } from 'preact'
import Icon from 'snippets/icon/icon'

/**
 * Back to top button — visible after one third of the scrollable page height.
 * Visibility is driven by data-ui="visible" and data-visible: Tailwind variants.
 */
export class BackToTopElement extends HTMLElement {
  static SCROLL_FRACTION = 1 / 5
  static BUTTON_CLASS =
    'btn btn-fill btn-primary btn-m btn-square fixed bottom-margin left-margin z-40 opacity-0 invisible pointer-events-none transition-opacity data-visible:opacity-100 data-visible:visible data-visible:pointer-events-auto'

  private button: HTMLButtonElement | null = null
  private ticking = false
  private readonly boundHandleScroll = () => this.handleScroll()
  private readonly boundHandleResize = () => this.updateVisibility()
  private readonly boundHandleClick = () => this.scrollToTop()

  connectedCallback() {
    const label = this.textContent?.trim() || this.getAttribute('label') || 'Back to top'
    this.textContent = ''
    this.renderButton(label)
    this.updateVisibility()
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true })
    window.addEventListener('resize', this.boundHandleResize, { passive: true })
    this.button?.addEventListener('click', this.boundHandleClick)
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundHandleScroll)
    window.removeEventListener('resize', this.boundHandleResize)
    this.button?.removeEventListener('click', this.boundHandleClick)
  }

  private get scrollThreshold(): number {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    return Math.max(0, scrollable * BackToTopElement.SCROLL_FRACTION)
  }

  private renderButton(label: string) {
    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.className = BackToTopElement.BUTTON_CLASS
    this.button.setAttribute('aria-label', label)
    this.button.setAttribute('aria-hidden', 'true')

    const iconWrap = document.createElement('span')
    iconWrap.className = 'flex size-[14px] children:size-full'
    render(h(Icon, { id: 'arrow-up', className: 'size-full' }), iconWrap)
    this.button.appendChild(iconWrap)

    this.appendChild(this.button)
  }

  private handleScroll() {
    if (this.ticking) return
    this.ticking = true
    window.requestAnimationFrame(() => {
      this.updateVisibility()
      this.ticking = false
    })
  }

  private updateVisibility() {
    if (!this.button) return

    const visible = window.scrollY >= this.scrollThreshold
    if (visible) {
      this.button.dataset.ui = 'visible'
      this.button.setAttribute('aria-hidden', 'false')
    } else {
      delete this.button.dataset.ui
      this.button.setAttribute('aria-hidden', 'true')
    }
  }

  private scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
